import {
  Box,
  Button,
  Card,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { DEFAULT_CHECKOUT_PATHNAME } from "@/utils/formHref";
import { stringifyBase64 } from "@/hooks/useQueryParamState";
import { Highlighter } from "@/ui/Highlighter";
import useIsFlagVariantTest from "@/hooks/useIsFlagVariantTest";
import usePlanCardPrice from "@/hooks/usePlanCardPrice";
import { UpsellPlanCardProps } from "@/components/cards/UpsellPlanCard";
import { ENTRY_PLAN_CONFIGS } from "@/config/entryPlanConfigs";
import { FlexBetween } from "@/components/common/styles";
import { ProxyTypeCard } from "@/ui/browse/ProxyTypeCard";

const BulletItem = ({ children }: { children: React.ReactNode }) => (
  <Stack direction="row" spacing={2} alignItems="center">
    <Box
      sx={{
        width: 5,
        height: 5,
        borderRadius: "50%",
        bgcolor: "primary.dark",
        flexShrink: 0,
      }}
    />
    <Typography variant="body2" fontWeight={500} color="text.primary">
      {children}
    </Typography>
  </Stack>
);

const PlanCard = ({
  title,
  numProxies,
  bandwidth,
  query,
  imgName,
  pricingConfig,
  isYearly = false,
}: UpsellPlanCardProps) => {
  const navigate = useNavigate();

  const { price, discountPrice } = usePlanCardPrice(pricingConfig, isYearly);

  const handleGetStarted = () => {
    navigate({
      to: DEFAULT_CHECKOUT_PATHNAME,
      search: query,
    });
  };

  return (
    <ProxyTypeCard
      selected={false}
      inverted
      sx={{
        p: 0,
        // Subgrid: the card's sections occupy the parent grid's row tracks so
        // each divider aligns with the matching one on the sibling cards.
        display: "grid",
        gridTemplateRows: "subgrid",
        gridRow: "span 3",
        rowGap: 0,
        position: "relative",
      }}
    >
      <Stack spacing={2} sx={{ px: 3, pt: 3, pb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <img
            loading="lazy"
            decoding="async"
            src={`/${imgName}-icon.svg`}
            width={28}
            height={28}
            alt={imgName}
          />
          <Typography
            variant="subtitle1"
            sx={{ fontSize: 17, fontWeight: 500 }}
          >
            {title}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        spacing={1}
        sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}
      >
        <BulletItem>{numProxies}</BulletItem>
        <BulletItem>{bandwidth}</BulletItem>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}
      >
        <Stack direction="row" alignItems="baseline" spacing={0.5}>
          {price === undefined ? (
            <Skeleton variant="text" width={60} />
          ) : (
            <>
              {discountPrice ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textDecoration: "line-through" }}
                >
                  {price}
                </Typography>
              ) : null}
              <Typography variant="h5" color="text.primary" fontWeight={700}>
                {discountPrice ?? price}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                /mo
              </Typography>
            </>
          )}
        </Stack>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={handleGetStarted}
        >
          Get Started
        </Button>
      </Stack>
    </ProxyTypeCard>
  );
};

export default function MostPopularPlansInline({
  title = "Most Popular Plans",
}: {
  title?: string;
}) {
  const inExp = useIsFlagVariantTest("changing-annual-discount");

  const [isYearly, setIsYearly] = useState(false);

  const extraParams = isYearly ? { isYearly: true } : {};

  const proxyServer = {
    imgName: "datacenter",
    title: "Proxy Server",
    numProxies: "100 Proxies",
    bandwidth: "250 GB Bandwidth",
    pricingConfig: ENTRY_PLAN_CONFIGS.datacenter,
    query: {
      source: "direct-links-banner",
      customizeParams: stringifyBase64({
        planCategory: "shared",
        planSubCategory: "default",
        numberOfProxies: 100,
        bandwidth: 250,
        subUser: 3,
        proxyReplacements: 10,
        ...extraParams,
      }),
    },
  };

  const staticResidential = {
    imgName: "static-residential",
    title: "Static Residential",
    numProxies: "20 Proxies",
    bandwidth: "250 GB Bandwidth",
    pricingConfig: ENTRY_PLAN_CONFIGS.staticResidential,
    query: {
      source: "direct-links-banner",
      customizeParams: stringifyBase64({
        planCategory: "shared",
        planSubCategory: "isp",
        numberOfProxies: 20,
        bandwidth: 250,
        subUser: 3,
        proxyReplacements: 10,
        ...extraParams,
      }),
    },
  };

  const rotatingResidential = {
    imgName: "residential",
    title: "Rotating Residential",
    numProxies: "80M+ IPs/month",
    bandwidth: "1 GB bandwidth",
    pricingConfig: ENTRY_PLAN_CONFIGS.rotatingResidential,
    query: {
      source: "direct-links-banner",
      customizeParams: stringifyBase64({
        planCategory: "shared",
        planSubCategory: "residential",
        numberOfProxies: 80000000,
        bandwidth: 1,
        subUser: 3,
        proxyReplacements: 0,
        ...extraParams,
      }),
    },
  };

  const data = [proxyServer, staticResidential, rotatingResidential];

  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={3}>
        <FlexBetween sx={{ flexWrap: "wrap", gap: 1 }}>
          <Typography variant="h6">{title}</Typography>
          <Box sx={{ bgcolor: "grey.100", borderRadius: 1, px: 1.5, py: 0.5 }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  data-testid="yearly-toggle"
                  checked={isYearly}
                  onChange={() => setIsYearly((prev) => !prev)}
                />
              }
              label={
                <Typography variant="body2">
                  Pay Yearly{" "}
                  <Highlighter
                    size="small"
                    severity="secondary"
                    innerVariant="filled"
                    sx={{ ml: 1 }}
                  >
                    {`Save ${inExp ? "20%" : "30%"}`}
                  </Highlighter>
                </Typography>
              }
            />
          </Box>
        </FlexBetween>

        <Box
          sx={{
            display: "grid",
            // 16px between stacked cards on mobile, 8px once they sit in a row.
            gap: { xs: 3, sm: 2 },
            // Tablet (2-col) up to 1168px, desktop (3-col) at/above it. Scoped
            // to this component with a raw query so the global theme
            // breakpoints stay untouched.
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
            },
            "@media (min-width: 1168px)": {
              gridTemplateColumns: "repeat(3, 1fr)",
            },
            // Each card maps its sections onto these shared row tracks via
            // `grid-template-rows: subgrid`, so the dividers line up across
            // cards even when the use-cases chips wrap differently.
            gridTemplateRows: "repeat(3, auto)",
          }}
        >
          {data.map((item) => (
            <PlanCard key={item.imgName} {...item} isYearly={isYearly} />
          ))}
        </Box>
      </Stack>
    </Card>
  );
}
